import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiPlus,
  FiEye,
  FiEdit,
  FiTrash2,
  FiSearch,
  FiFilter,
  FiTrendingUp,
  FiHeart,
  FiBookOpen,
} from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import articleService from '../../services/articleService';
import { Article } from '../../types/article.types';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import Loader from '../../components/common/Loader/Loader';
import styles from './ArticleList.module.css';

interface FilterState {
  search: string;
  category: string;
  status: string;
  sortBy: string;
  order: 'asc' | 'desc';
}

const ArticleList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    category: '',
    status: '',
    sortBy: 'createdAt',
    order: 'desc',
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalArticles: 0,
    articlesPerPage: 10,
  });

  useEffect(() => {
    fetchArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.category, filters.status, filters.sortBy, filters.order, pagination.currentPage]);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const response = await articleService.getArticles({
        page: pagination.currentPage,
        limit: 10,
        category: filters.category || undefined,
        status: filters.status || undefined,
        search: filters.search || undefined,
        sortBy: filters.sortBy,
        order: filters.order,
      });

      setArticles(response.data?.articles || []);

      if (response.pagination) {
        setPagination({
          currentPage: response.pagination.currentPage,
          totalPages: response.pagination.totalPages,
          totalArticles: response.pagination.totalArticles || 0,
          articlesPerPage: response.pagination.articlesPerPage || 10,
        });
      }
    } catch (error: any) {
      console.error('Failed to fetch articles:', error);
      toast.error(error.response?.data?.message || 'Failed to load articles');
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    fetchArticles();
  };

  const handleDelete = async (id: string, headline: string) => {
    if (!window.confirm(`Are you sure you want to delete "${headline}"?\n\nThis action cannot be undone.`)) return;

    try {
      console.log('🗑️ Deleting article with ID:', id);
      await articleService.deleteArticle(id); // Delete by ID
      toast.success('Article deleted successfully');
      fetchArticles();
    } catch (error: any) {
      console.error('❌ Delete failed:', error);
      toast.error(error.response?.data?.message || 'Failed to delete article');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return styles.statusPublished;
      case 'draft':
        return styles.statusDraft;
      case 'archived':
        return styles.statusArchived;
      default:
        return '';
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      tech: '#3B82F6',
      innovation: '#8B5CF6',
      'early-birds-startup': '#10B981',
      unicorn: '#F59E0B',
      recognition: '#EF4444',
      startuprahi: '#F97316',
      manufacturing: '#6366F1',
      'stock-market': '#14B8A6',
      marketing: '#EC4899',
      trending: '#DC2626',
      featured: '#7C3AED',
      stories: '#0891B2',
      'women-empowerment': '#DB2777',
      farming: '#65A30D',
      'case-study': '#0369A1',
    };
    return colors[category] || '#64748b';
  };

  const getCategoryLabel = (category: string) => {
    return category.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div className={styles.articleList}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>
            <FiTrendingUp className={styles.titleIcon} />
            Top News & Articles
          </h1>
          <p className={styles.subtitle}>
            Manage and publish your news articles • Total: <strong>{pagination.totalArticles}</strong> articles
          </p>
        </div>
        <div className={styles.headerRight}>
          <Button variant="primary" icon={<FiPlus />} onClick={() => navigate('/articles/create')}>
            Create New Article
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {/* <div className={styles.statsGrid}>
        <Card className={styles.statsCard}>
          <div className={styles.statsContent}>
            <div className={styles.statsIcon} style={{ background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)' }}>
              <FiTrendingUp />
            </div>
            <div className={styles.statsData}>
              <p className={styles.statsLabel}>Total Articles</p>
              <h3 className={styles.statsValue}>{pagination.totalArticles}</h3>
            </div>
          </div>
        </Card>

        <Card className={styles.statsCard}>
          <div className={styles.statsContent}>
            <div className={styles.statsIcon} style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}>
              <FiEye />
            </div>
            <div className={styles.statsData}>
              <p className={styles.statsLabel}>Published</p>
              <h3 className={styles.statsValue}>
                {articles.filter((a) => a.status === 'published').length}
              </h3>
            </div>
          </div>
        </Card>

        <Card className={styles.statsCard}>
          <div className={styles.statsContent}>
            <div className={styles.statsIcon} style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' }}>
              <FiEdit />
            </div>
            <div className={styles.statsData}>
              <p className={styles.statsLabel}>Drafts</p>
              <h3 className={styles.statsValue}>{articles.filter((a) => a.status === 'draft').length}</h3>
            </div>
          </div>
        </Card>

        <Card className={styles.statsCard}>
          <div className={styles.statsContent}>
            <div className={styles.statsIcon} style={{ background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)' }}>
              <FiHeart />
            </div>
            <div className={styles.statsData}>
              <p className={styles.statsLabel}>Total Likes</p>
              <h3 className={styles.statsValue}>
                {articles.reduce((sum, a) => sum + (a.likes || 0), 0).toLocaleString()}
              </h3>
            </div>
          </div>
        </Card>
      </div> */}

      {/* Filters */}
      <Card className={styles.filtersCard}>
        <form onSubmit={handleSearch} className={styles.filtersForm}>
          <div className={styles.searchBox}>
            <FiSearch className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search articles by title, description, tags..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className={styles.searchInput}
            />
            <Button type="submit" variant="primary" size="small">
              Search
            </Button>
          </div>

          <div className={styles.filterRow}>
            <div className={styles.filterGroup}>
              
              <select
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                className={styles.filterSelect}
              >
                <option value="">All Categories</option>
                <option value="tech">Technology</option>
                  <option value="innovation">Innovation</option>
                  <option value="unicorn">Unicorn</option>
                  <option value="trending">Trending</option>
                  <option value="featured">Featured</option>
                  <option value="women-empowerment">Women Empowerment</option>
                  <option value="farming">Farming</option>
                  <option value="case-study">Case Study</option>
                  <option value="early-birds-startup">Early Birds Startup</option>
                  <option value="recognition">Recognition</option>
                  <option value="startuprahi">StartupRahi</option>
                  <option value="manufacturing">Manufacturing</option>
                  <option value="stock-market">Stock Market</option>
                  <option value="marketing">Marketing</option>
                  <option value="stories">Stories</option>
                  <option value="top">Top News</option>
              </select>
            </div>

            <div className={styles.filterGroup}>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className={styles.filterSelect}
              >
                <option value="">All Status</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            <div className={styles.filterGroup}>
              <select
                value={filters.sortBy}
                onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
                className={styles.filterSelect}
              >
                <option value="createdAt">Created Date</option>
                <option value="publishedAt">Published Date</option>
                <option value="views">Views</option>
                <option value="likes">Likes</option>
              </select>
            </div>

            <div className={styles.filterGroup}>
              <select
                value={filters.order}
                onChange={(e) => setFilters({ ...filters, order: e.target.value as 'asc' | 'desc' })}
                className={styles.filterSelect}
              >
                <option value="desc">Newest First</option>
                <option value="asc">Oldest First</option>
              </select>
            </div>
          </div>
        </form>
      </Card>

      {/* Articles Table */}
      {loading ? (
        <div className={styles.loaderContainer}>
          <Loader size="large" text="Loading articles..." />
        </div>
      ) : articles.length === 0 ? (
        <Card className={styles.emptyState}>
          <FiTrendingUp className={styles.emptyIcon} />
          <h3>No Articles Found</h3>
          <p>
            {filters.search || filters.category || filters.status
              ? 'Try adjusting your filters'
              : 'Start by creating your first article'}
          </p>
          <Button variant="primary" icon={<FiPlus />} onClick={() => navigate('/articles/create')}>
            Create New Article
          </Button>
        </Card>
      ) : (
        <>
          <Card className={styles.tableCard}>
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.thThumbnail}>Image</th>
                    <th className={styles.thArticle}>Article Title</th>
                    <th className={styles.thCategory}>Category</th>
                    <th className={styles.thAuthor}>Author</th>
                    <th className={styles.thStats}>Stats</th>
                    <th className={styles.thStatus}>Status</th>
                    <th className={styles.thActions}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {articles.map((article) => (
                    <tr key={article.id} className={styles.tableRow}>
                      {/* Thumbnail */}
                      <td className={styles.tdThumbnail}>
                        <div className={styles.thumbnail}>
                          <img
                            src={article.featuredImage.thumbnailUrl || article.featuredImage.url}
                            alt={article.headline}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src =
                                'https://via.placeholder.com/120x80/3B82F6/FFFFFF?text=No+Image';
                            }}
                          />
                          {article.isFeatured && <span className={styles.featuredBadge}>⭐</span>}
                          {article.isBreakingNews && <span className={styles.breakingBadge}>🔥</span>}
                        </div>
                      </td>

                      {/* Article Title - Click navigates to view by SLUG */}
                      <td className={styles.tdArticle}>
                        <h4
                          className={styles.articleTitle}
                          onClick={() => navigate(`/articles/view/${article.slug}`)}
                        >
                          {article.headline}
                        </h4>
                      </td>

                      {/* Category */}
                      <td className={styles.tdCategory}>
                        <span
                          className={styles.categoryBadge}
                          style={{ backgroundColor: getCategoryColor(article.category) }}
                        >
                          {getCategoryLabel(article.category)}
                        </span>
                      </td>

                      {/* Author */}
                      <td className={styles.tdAuthor}>
                        <div className={styles.authorCell}>
                          <p className={styles.authorName}>{article.author?.name || 'Unknown Author'}</p>
                          <p className={styles.authorRole}>{article.author?.role || 'N/A'}</p>
                        </div>
                      </td>

                      {/* Stats */}
                      <td className={styles.tdStats}>
                        <div className={styles.statsCell}>
                          <div className={styles.statItem}>
                            <FiEye />
                            <span>{(article.views || 0).toLocaleString()}</span>
                          </div>
                          <div className={styles.statItem}>
                            <FiHeart />
                            <span>{(article.likes || 0).toLocaleString()}</span>
                          </div>
                          <div className={styles.statItem}>
                            <FiBookOpen />
                            <span>{article.readTime} min</span>
                          </div>
                        </div>
                      </td>

                      {/* Status */}
                      <td className={styles.tdStatus}>
                        <span className={`${styles.statusBadge} ${getStatusColor(article.status)}`}>
                          {article.status}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className={styles.tdActions}>
                        <div className={styles.actionsCell}>
                          {/* View by SLUG */}
                          <button
                            className={styles.actionBtn}
                            onClick={() => navigate(`/articles/view/${article.slug}`)}
                            title="View Article"
                          >
                            <FiEye />
                          </button>
                          
                          {/* Edit by ID */}
                          <button
                            className={styles.actionBtn}
                            onClick={() => navigate(`/articles/edit/${article.slug}`)}
                            title="Edit Article"
                          >
                            <FiEdit />
                          </button>
                          
                          {/* Delete by ID */}
                          {(user?.role === 'superadmin' || user?.role === 'developer') && (
                            <button
                              className={`${styles.actionBtn} ${styles.deleteBtn}`}
                              onClick={() => handleDelete(article.id, article.headline)}
                              title="Delete Article"
                            >
                              <FiTrash2 />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className={styles.pagination}>
              <Button
                variant="ghost"
                disabled={pagination.currentPage === 1}
                onClick={() =>
                  setPagination((prev) => ({ ...prev, currentPage: prev.currentPage - 1 }))
                }
              >
                Previous
              </Button>

              <div className={styles.pageNumbers}>
                {(() => {
                  const pages: number[] = [];
                  const maxVisible = 5;
                  let startPage = Math.max(1, pagination.currentPage - Math.floor(maxVisible / 2));
                  let endPage = Math.min(pagination.totalPages, startPage + maxVisible - 1);

                  if (endPage - startPage < maxVisible - 1) {
                    startPage = Math.max(1, endPage - maxVisible + 1);
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(i);
                  }

                  return pages.map((page) => (
                    <button
                      key={page}
                      className={`${styles.pageButton} ${
                        page === pagination.currentPage ? styles.activePage : ''
                      }`}
                      onClick={() => setPagination((prev) => ({ ...prev, currentPage: page }))}
                    >
                      {page}
                    </button>
                  ));
                })()}
              </div>

              <Button
                variant="ghost"
                disabled={pagination.currentPage === pagination.totalPages}
                onClick={() =>
                  setPagination((prev) => ({ ...prev, currentPage: prev.currentPage + 1 }))
                }
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ArticleList;